import {
  Bell,
  Camera,
  Edit,
  Loader2,
  Mail,
  Plus,
  Share2,
  Trash2,
  Users,
} from "lucide-react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { LinkifiedText } from "@/components/common/LinkifiedText";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Card } from "@/features/study-group-events/components/Card";
import type { EventApiResponse, UserShort } from "@/lib/humhub-api/types";
import type { dedupeReminderItems } from "@/lib/utils/event/form-initializer";
import { formatReminderText } from "@/lib/utils/eventUtils";
import { getLanguageLabel } from "@/lib/utils/textUtils";

const DESCRIPTION_PREVIEW_LENGTH = 200;
const URL_REGEX = /https?:\/\/[^\s<>"{}|\\^`[\]]+/g;

const truncateDescription = (
  description: string,
  maxLength = DESCRIPTION_PREVIEW_LENGTH,
) => {
  if (description.length <= maxLength) return description;
  let cutoff = maxLength;
  URL_REGEX.lastIndex = 0;

  for (const match of description.matchAll(URL_REGEX)) {
    const matchStart = match.index ?? 0;
    const matchEnd = matchStart + match[0].length;
    if (matchStart < cutoff && matchEnd > cutoff) {
      cutoff = matchEnd;
      break;
    }
    if (matchStart >= cutoff) {
      break;
    }
  }

  const truncated = description.slice(0, cutoff);
  return truncated.trimEnd();
};

const EventHeroImage: React.FC<{
  eventTitle: string;
  fileBlobUrl: string | null | undefined;
  imageLoading: boolean;
  imageError: unknown;
  replaceFileLoading: boolean;
  removeFileLoading: boolean;
  isOwner: boolean;
  onReplaceImage: () => void;
  onDeleteImage: () => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  fileError: string | null;
  replaceFileError: Error | null | undefined;
  removeFileError: Error | null | undefined;
  isInteractionDisabled: boolean;
}> = ({
  eventTitle,
  fileBlobUrl,
  imageLoading,
  imageError,
  replaceFileLoading,
  removeFileLoading,
  isOwner,
  onReplaceImage,
  onDeleteImage,
  onFileChange,
  fileInputRef,
  fileError,
  replaceFileError,
  removeFileError,
  isInteractionDisabled,
}) => {
  const { t } = useTranslation();

  return (
    <div className="relative grid h-full w-1/3 place-items-center">
      {imageLoading && (
        <div className="flex w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-rw-light-gray p-5">
          <div className="mb-2 size-8 animate-spin rounded-full border-b-2 border-gray-900" />
          <p className="mt-2 text-sm text-gray-500">{t("common.loading")}</p>
        </div>
      )}
      {(replaceFileLoading || removeFileLoading) && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-black/50 text-white">
          <div className="flex flex-col items-center">
            <div className="mb-2 size-8 animate-spin rounded-full border-b-2 border-white" />
            <span>{t("common.updating")}</span>
          </div>
        </div>
      )}
      {!imageLoading && !imageError && fileBlobUrl && (
        <div className="group relative">
          <img
            src={fileBlobUrl}
            alt={eventTitle}
            className="h-auto w-full rounded-lg object-cover"
          />
          {isOwner && (
            <div className="absolute right-2 top-2 flex gap-2">
              <button
                type="button"
                className="cursor-pointer rounded-full bg-white p-1.5 shadow-md hover:bg-gray-100"
                onClick={onDeleteImage}
                disabled={isInteractionDisabled}
              >
                <Trash2 className="size-5 text-red-600" />
              </button>
              <button
                type="button"
                className="cursor-pointer rounded-full bg-white p-1.5 shadow-md hover:bg-gray-100"
                onClick={onReplaceImage}
                disabled={isInteractionDisabled}
              >
                <Camera className="size-5 text-gray-700" />
              </button>
            </div>
          )}
        </div>
      )}
      {!imageLoading && !fileBlobUrl && isOwner && (
        <button
          type="button"
          className="flex w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-rw-light-gray py-5 hover:bg-gray-50"
          onClick={onReplaceImage}
          disabled={isInteractionDisabled}
          aria-label="Add event image"
        >
          <Camera className="size-10 text-gray-400" />
          <p className="mt-2 text-sm text-gray-500">
            {t("common.uploadImage")}
          </p>
        </button>
      )}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/png,image/jpeg,image/jpg"
        onChange={onFileChange}
      />
      {fileError && <p className="mt-1 text-sm text-red-500">{fileError}</p>}
      {replaceFileError && (
        <p className="mt-1 text-sm text-red-500">
          {t("common.error")}: {replaceFileError.message}
        </p>
      )}
      {removeFileError && (
        <p className="mt-1 text-sm text-red-500">
          {t("common.error")}: {removeFileError.message}
        </p>
      )}
    </div>
  );
};

export const EventHero: React.FC<{
  event: EventApiResponse;
  isDraft: boolean;
  isOwner: boolean;
  imageLoading: boolean;
  imageError: unknown;
  fileBlobUrl: string | null | undefined;
  replaceFileLoading: boolean;
  removeFileLoading: boolean;
  fileError: string | null;
  replaceFileError: Error | null | undefined;
  removeFileError: Error | null | undefined;
  onReplaceImage: () => void;
  onDeleteImage: () => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCopyEventLink: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onStudyGroupClick?: (container: EventApiResponse["container"]) => void;
  isInteractionDisabled?: boolean;
}> = ({
  event,
  isDraft,
  isOwner,
  imageLoading,
  imageError,
  fileBlobUrl,
  replaceFileLoading,
  removeFileLoading,
  fileError,
  replaceFileError,
  removeFileError,
  onReplaceImage,
  onDeleteImage,
  onFileChange,
  onCopyEventLink,
  fileInputRef,
  onStudyGroupClick,
  isInteractionDisabled = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center rounded-tr-lg bg-rw-white p-2">
      <div className="flex flex-1 items-center justify-between gap-4 p-5">
        <div className="flex flex-col gap-y-0.5">
          <div className="mb-1 flex items-center gap-2">
            <h3 className="text-xl font-semibold text-rw-dark-gray">
              {event.title}
            </h3>
            {isDraft && (
              <span className="rounded-full bg-orange-50 px-2 py-0.5 text-xs text-orange-700">
                {t("common.draft")}
              </span>
            )}
          </div>

          <div className="flex text-sm text-rw-dark-gray">
            <span className="mr-3 whitespace-nowrap">
              {t("common.language")} :
            </span>
            {getLanguageLabel(event.language)}
          </div>

          <div className="flex text-sm text-rw-dark-gray">
            <span className="mr-3 whitespace-nowrap">
              {t("common.organizer")} :
            </span>
            {onStudyGroupClick && event.container?.name ? (
              <Button
                variant="link"
                size="xs"
                className="p-0 text-rw-main underline"
                onClick={() => onStudyGroupClick(event.container)}
                disabled={isInteractionDisabled}
              >
                <span>{event.container.name}</span>
              </Button>
            ) : (
              (event.container?.name ?? t("common.notSet"))
            )}
          </div>
        </div>

        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onCopyEventLink}
                disabled={isInteractionDisabled}
                aria-label={t("common.copyLink")}
              >
                <Share2 className="size-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("common.copyLink")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <EventHeroImage
        eventTitle={event.title}
        fileBlobUrl={fileBlobUrl}
        imageLoading={imageLoading}
        imageError={imageError}
        replaceFileLoading={replaceFileLoading}
        removeFileLoading={removeFileLoading}
        isOwner={isOwner}
        onReplaceImage={onReplaceImage}
        onDeleteImage={onDeleteImage}
        onFileChange={onFileChange}
        fileInputRef={fileInputRef}
        fileError={fileError}
        replaceFileError={replaceFileError}
        removeFileError={removeFileError}
        isInteractionDisabled={isInteractionDisabled}
      />
    </div>
  );
};

export const EventDescription: React.FC<{
  description?: string;
  showFullDescription: boolean;
  onToggle: () => void;
  isInteractionDisabled?: boolean;
}> = ({
  description,
  showFullDescription,
  onToggle,
  isInteractionDisabled = false,
}) => {
  const { t } = useTranslation();
  if (!description) return null;

  return (
    <div className="flex-1 text-base font-light text-rw-black-gray">
      <LinkifiedText
        text={
          showFullDescription ? description : truncateDescription(description)
        }
        className="block"
      />
      {description.length > DESCRIPTION_PREVIEW_LENGTH && (
        <Button
          variant="link"
          className="mt-2 h-auto p-0 text-sm text-rw-main underline"
          onClick={onToggle}
          disabled={isInteractionDisabled}
        >
          {showFullDescription ? t("common.showLess") : t("common.showMore")}
        </Button>
      )}
    </div>
  );
};

const getNormalizedUrl = (value: string | null | undefined): string | null => {
  if (!value) return null;
  try {
    const normalized = value.startsWith("http") ? value : `https://${value}`;
    const url = new URL(normalized);
    if (url.protocol === "http:" || url.protocol === "https:") {
      return normalized;
    }
  } catch {
    // invalid URL
  }
  return null;
};

const formatOnlineLocation = (onlineLocation: string | null | undefined) => {
  if (!onlineLocation) return null;
  const href = getNormalizedUrl(onlineLocation);
  if (!href) return onlineLocation;
  return (
    <a
      href={href}
      className="text-blue-600 hover:underline"
      target="_blank"
      rel="noopener noreferrer nofollow"
    >
      {onlineLocation}
    </a>
  );
};

export const EventSidebar: React.FC<{
  formattedDate: string;
  formatLabel: string;
  location?: string | null;
  onlineLocation?: string | null;
  showLocationDetails?: boolean;
}> = ({
  formattedDate,
  formatLabel,
  location,
  onlineLocation,
  showLocationDetails = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex w-1/3 flex-col gap-4">
      <Card title={t("common.dateTime")}>{formattedDate}</Card>

      <Card title={t("common.format")}>
        <div className="text-left text-base font-normal text-rw-black-gray">
          {formatLabel}
        </div>

        {showLocationDetails && (location || onlineLocation) && (
          <div className="mt-3 space-y-1 text-sm text-rw-black-gray">
            {location ? (
              <div>
                <span className="font-semibold">
                  {t("eventCreate.physicalLocation")}:
                </span>{" "}
                <span>{location}</span>
              </div>
            ) : null}
            {onlineLocation ? (
              <div>
                <span className="font-semibold">{t("common.eventUrl")}:</span>{" "}
                <span>{formatOnlineLocation(onlineLocation)}</span>
              </div>
            ) : null}
          </div>
        )}
      </Card>
    </div>
  );
};

export const AttendanceSection: React.FC<{
  isAttending: boolean;
  shouldRenderAttendCta: boolean;
  respondLoading: boolean;
  respondError: Error | null;
  onAttendClick: () => void;
  includeDuplicateButton: boolean;
  showDuplicateButton: boolean;
  onDuplicate: () => void;
  isInteractionDisabled: boolean;
}> = ({
  isAttending,
  shouldRenderAttendCta,
  respondLoading,
  respondError,
  onAttendClick,
  includeDuplicateButton,
  showDuplicateButton,
  onDuplicate,
  isInteractionDisabled,
}) => {
  const { t } = useTranslation();
  if (!shouldRenderAttendCta) {
    return null;
  }

  return (
    <div>
      {isAttending ? (
        <p className="mb-4 text-gray-700">{t("eventManage.youAreAttending")}</p>
      ) : (
        <p className="mb-4 text-gray-700">{t("eventManage.doYouAttend")}</p>
      )}
      <div className="flex justify-center">
        <Button
          variant={isAttending ? "destructive" : "teal"}
          onClick={onAttendClick}
          disabled={respondLoading || isInteractionDisabled}
        >
          {respondLoading
            ? t("common.updating")
            : isAttending
              ? t("eventManage.cancelAttendance")
              : t("eventManage.commitAttendance")}
        </Button>
      </div>
      {!isAttending && (
        <p className="mt-4 text-center text-sm text-gray-500">
          {t("eventManage.doYouAttendHelp")}
        </p>
      )}
      {respondError && (
        <p className="mt-2 text-sm text-red-500">
          {t("common.error")}:{" "}
          {respondError instanceof Error
            ? respondError.message
            : String(respondError)}
        </p>
      )}
      {includeDuplicateButton && showDuplicateButton && (
        <div className="mt-4 border-t border-gray-200 pt-4">
          <div className="flex justify-center">
            <button
              type="button"
              onClick={onDuplicate}
              disabled={isInteractionDisabled}
              className="group inline-flex items-center gap-2 text-sm text-rw-main transition hover:underline"
            >
              {t("eventManage.duplicateEvent")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const EventManagementMenu: React.FC<{
  isDraft: boolean;
  showDuplicateButton: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  isInteractionDisabled: boolean;
}> = ({
  isDraft,
  showDuplicateButton,
  onEdit,
  onDuplicate,
  onDelete,
  isInteractionDisabled,
}) => {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <Edit className="size-5 text-rw-main" />
        <h3 className="font-semibold text-rw-dark-gray">
          {t("eventManage.eventManagement")}
        </h3>
      </div>

      <div className="space-y-3">
        {isDraft && (
          <div className="rounded-md bg-orange-50 p-2 text-sm text-orange-700">
            {t("eventManage.draftDescription")}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            disabled={isInteractionDisabled}
          >
            {isDraft
              ? t("eventManage.editAndPublishEvent")
              : t("eventManage.editEvent")}
          </Button>

          {showDuplicateButton && (
            <Button
              variant="outline"
              size="sm"
              onClick={onDuplicate}
              disabled={isInteractionDisabled}
            >
              {t("eventManage.duplicateEvent")}
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            className="border-red-300 text-red-600 hover:bg-red-50"
            onClick={onDelete}
            disabled={isInteractionDisabled}
          >
            {t("eventManage.deleteEvent")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const AttendanceManagementSection: React.FC<{
  attendingParticipants: UserShort[];
  uniqueReminders: ReturnType<typeof dedupeReminderItems>;
  onShowAttendees: () => void;
  onOpenSelectAttendees: () => void;
  onOpenAutoNotifications: () => void;
  onOpenSendEmailNow: () => void;
  isInteractionDisabled: boolean;
  acceptAllParticipantsLoading: boolean;
  canBulkAcceptParticipants: boolean;
}> = ({
  attendingParticipants,
  uniqueReminders,
  onShowAttendees,
  onOpenSelectAttendees,
  onOpenAutoNotifications,
  onOpenSendEmailNow,
  isInteractionDisabled,
  acceptAllParticipantsLoading,
  canBulkAcceptParticipants,
}) => {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <Users className="size-5 text-rw-main" />
        <h3 className="font-semibold text-rw-dark-gray">
          {t("eventManage.attendanceManagement")}
        </h3>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* 出席者 */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={onShowAttendees}
            disabled={isInteractionDisabled}
          >
            <Users className="size-4" />
            <span>
              {t("eventManage.attendees")}: {attendingParticipants.length}
            </span>
          </Button>

          <Button
            variant="outline"
            size="icon"
            className="size-9 border-gray-200"
            onClick={onOpenSelectAttendees}
            disabled={
              isInteractionDisabled ||
              acceptAllParticipantsLoading ||
              !canBulkAcceptParticipants
            }
            title={t("eventManage.addAttendees")}
            aria-label={t("eventManage.addAttendees")}
          >
            {acceptAllParticipantsLoading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Plus className="size-4" />
            )}
          </Button>
        </div>

        {/* 自動お知らせ */}
        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
          onClick={onOpenAutoNotifications}
          disabled={isInteractionDisabled}
        >
          <Bell className="size-4" />
          <span>{t("eventManage.autoNotifications")}:</span>
          {uniqueReminders.length === 0 && (
            <span className="text-xs">{t("common.notSet")}</span>
          )}
          {uniqueReminders.length > 0 &&
            uniqueReminders.map((reminder) => (
              <span
                key={`${reminder.unit}-${reminder.value}`}
                className="text-xs"
              >
                {formatReminderText(reminder, t)} {t("common.before")}
              </span>
            ))}
        </Button>

        {/* メール送信ボタン */}
        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
          onClick={onOpenSendEmailNow}
          disabled={isInteractionDisabled}
        >
          <Mail className="size-4" />
          <span>{t("eventManage.sendNotificationNow")}</span>
        </Button>
      </div>
    </div>
  );
};

export const DefaultFooter: React.FC<{
  canManageEvent: boolean;
  shouldRenderAttendCta: boolean;
  attendingParticipants: UserShort[];
  uniqueReminders: ReturnType<typeof dedupeReminderItems>;
  isDraft: boolean;
  showDuplicateButton: boolean;
  handleAttendClick: () => void;
  handleShowAttendees: () => void;
  handleOpenSelectAttendees: () => void;
  handleDuplicate: () => void;
  handleEdit: () => void;
  handleDelete: () => void;
  handleOpenAutoNotifications: () => void;
  handleOpenSendEmailNow: () => void;
  respondLoading: boolean;
  respondError: Error | null;
  isAttending: boolean;
  isInteractionDisabled: boolean;
  acceptAllParticipantsLoading: boolean;
  canBulkAcceptParticipants: boolean;
}> = ({
  canManageEvent,
  shouldRenderAttendCta,
  attendingParticipants,
  uniqueReminders,
  isDraft,
  showDuplicateButton,
  handleAttendClick,
  handleShowAttendees,
  handleOpenSelectAttendees,
  handleDuplicate,
  handleEdit,
  handleDelete,
  handleOpenAutoNotifications,
  handleOpenSendEmailNow,
  respondLoading,
  respondError,
  isAttending,
  isInteractionDisabled,
  acceptAllParticipantsLoading,
  canBulkAcceptParticipants,
}) => {
  const attendanceSection = (
    <AttendanceSection
      isAttending={isAttending}
      shouldRenderAttendCta={shouldRenderAttendCta}
      respondLoading={respondLoading}
      respondError={respondError}
      onAttendClick={handleAttendClick}
      includeDuplicateButton={!canManageEvent}
      showDuplicateButton={showDuplicateButton}
      onDuplicate={handleDuplicate}
      isInteractionDisabled={isInteractionDisabled}
    />
  );

  if (!canManageEvent && !shouldRenderAttendCta) {
    return null;
  }

  return (
    <div className="border-t bg-white pt-4">
      <div className="w-full text-center">
        {canManageEvent ? (
          <div className="text-rw-black-gray">
            {attendanceSection && (
              <div className="mb-4">{attendanceSection}</div>
            )}

            <div className="mt-6 border-t border-gray-200 pt-4">
              {/* 出席管理セクション */}
              <div className="mb-4">
                <AttendanceManagementSection
                  attendingParticipants={attendingParticipants}
                  uniqueReminders={uniqueReminders}
                  onShowAttendees={handleShowAttendees}
                  onOpenSelectAttendees={handleOpenSelectAttendees}
                  onOpenAutoNotifications={handleOpenAutoNotifications}
                  onOpenSendEmailNow={handleOpenSendEmailNow}
                  isInteractionDisabled={isInteractionDisabled}
                  acceptAllParticipantsLoading={acceptAllParticipantsLoading}
                  canBulkAcceptParticipants={canBulkAcceptParticipants}
                />
              </div>

              {/* イベント管理メニュー */}
              <div>
                <EventManagementMenu
                  isDraft={isDraft}
                  showDuplicateButton={showDuplicateButton}
                  onEdit={handleEdit}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                  isInteractionDisabled={isInteractionDisabled}
                />
              </div>
            </div>
          </div>
        ) : (
          attendanceSection
        )}
      </div>
    </div>
  );
};
